import React, { useState } from 'react';
import { getApiUrl } from '../config/api';
import QRCode from 'qrcode';
import environment from '../config/environment';

const COMPANY_NAME = environment.companyName || 'Moreseg Cia. Ltda.';

const TwoFASetup = ({ userId, secret, qrCode: backendQrCode, onSetupComplete, onCancel }) => {
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Usar QR code del backend o generar uno local
  React.useEffect(() => {
    console.log('TwoFASetup useEffect:', { backendQrCode: !!backendQrCode, secret: !!secret });
    if (backendQrCode) {
      console.log('Usando QR code del backend');
      setQrCode(backendQrCode);
    } else if (secret) {
      console.log('Generando QR code local');
      generateQRCode();
    }
  }, [secret, backendQrCode]);

  const generateQRCode = async () => {
    try {
      const label = encodeURIComponent(`${COMPANY_NAME} - Usuario`);
      const issuer = encodeURIComponent('Sistema de Proformas');
      const otpauthUrl = `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}`;
      const qrCodeUrl = await QRCode.toDataURL(otpauthUrl);
      setQrCode(qrCodeUrl);
    } catch (error) {
      console.error('Error generando QR code:', error);
      setError('Error generando código QR');
    }
  };

  const verify2FA = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(getApiUrl('TWO_FA', 'VERIFY_ACTIVATE'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId, 
          token: verificationCode 
        }),
      });

      const result = await response.json();
      console.log('TwoFASetup verify2FA result:', result);

      if (result.success) {
        setSuccess('2FA configurado exitosamente');
        setTimeout(() => {
          onSetupComplete();
        }, 2000);
      } else {
        setError(result.error || 'Código inválido');
      }
    } catch (error) {
      console.error('Error verificando 2FA:', error);
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4">
          <div className="text-center mb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Configurar Seguridad Adicional
            </h2>
            <p className="text-gray-600 text-sm">
              Tu contraseña ha sido cambiada. Ahora debes configurar la autenticación de dos factores.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{success}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* QR Code */}
            <div className="text-center">
              {qrCode && (
                <div className="inline-block p-3 bg-white border-2 border-gray-200 rounded-lg">
                  <img src={qrCode} alt="QR Code para 2FA" className="w-32 h-32" />
                </div>
              )}
            </div>

            {/* Manual Entry Key */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h3 className="font-medium text-gray-900 mb-2 text-sm">¿No puedes escanear?</h3>
              <p className="text-xs text-gray-600 mb-2">Ingresa manualmente esta clave:</p>
              <div className="bg-white border border-gray-300 rounded px-2 py-1 font-mono text-xs break-all">
                {secret}
              </div>
            </div>

            {/* Verification Code Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código de Verificación
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                placeholder="000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono tracking-widest"
                maxLength={6}
              />
              <p className="mt-1 text-xs text-gray-500">
                Ingresa el código de 6 dígitos de tu aplicación autenticadora
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancelar
              </button>
              <button
                onClick={verify2FA}
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verificando...' : 'Verificar y Activar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFASetup;
